import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import qs from "query-string";
import { useDebounce } from "react-use";
import { pickBy } from "lodash";
import { useMutation } from "@apollo/client";
import { toast } from "react-toastify";
import { Controller, FormProvider, useForm } from "react-hook-form";
import axios from "axios";

import { MUTATION_UPDATE_BADGE_TEMPLATE_OPTIONS } from "../../../../config/graphql/mutation";
import { QUERY_BROTHER_PRINTER_TEMPLATE_OPTIONS } from "../../../../config/graphql/query";

import { useClientId } from "../hooks/useClient";
import { upload } from "../../../../lib/hooks/useSocket";
import useHasRole from "../../../../lib/hooks/useHasRole";
import { getApiUrl } from "../../../../lib/common/getApiUrl";
import { useBrotherPrinterTemplateOptions } from "../../../../lib/hooks/useBrotherPrinterTemplateOptions";

import ImageCropPicker from "../../../../components/ImageCropPicker";

const DEFAULT_BADGE_OPTIONS: Pick<
  IBadgeOptions,
  "showFullName" | "showCompany" | "dateFormat"
> = {
  showFullName: true,
  showCompany: true,
  dateFormat: "DD_MM_YYYY",
};

const BrotherPrinterTemplateOption = React.memo(
  ({
    name,
    label,
    disabled,
    onChangePreview,
  }: {
    name: string;
    label: string;
    disabled?: boolean;
    onChangePreview?: (name: string, checked: boolean) => void;
  }) => (
    <Controller
      name={name}
      render={({ field: { value, onChange, ...rest } }) => {
        return (
          <div className="form-check form-check-inline">
            <input
              {...rest}
              id={name}
              disabled={disabled}
              type="checkbox"
              className="form-check-input"
              checked={value === true}
              onChange={(e) => {
                onChange(e);
                onChangePreview?.(name, e.target.checked);
              }}
            />
            <label className="form-check-label" htmlFor={`${name}`}>
              {label}
            </label>
          </div>
        );
      }}
    />
  ),
);

const ToggleBrotherPrinterButton: React.FC<{
  disable?: boolean;
  onClick: () => void;
}> = React.memo(({ disable, onClick }) => {
  return (
    <button
      className={`btn btn-primary ${disable ? "btn-danger" : ""}`}
      onClick={(e) => {
        e.preventDefault();
        onClick?.();
      }}
    >
      {disable ? "Disable" : "Enable"} Brother Printer
    </button>
  );
});

const CHECKBOX_LIMIT = 8;

const BrotherPrinter: React.FC = () => {
  const { t } = useTranslation(["client", "common"]);
  const clientId = useClientId();
  const isAdmin = useHasRole(["ADMIN"]);

  const badgeIFrame = useRef<HTMLIFrameElement>(null);
  const badgeForm = useForm<IBadgeOptions>({
    shouldUnregister: true,
    defaultValues: DEFAULT_BADGE_OPTIONS,
  });

  const brotherPrinterTemplateOptions = useBrotherPrinterTemplateOptions({
    clientId,
    onCompleted: (data) => {
      badgeForm.reset(data.brotherPrinterTemplateOptions);
    },
  });

  const badgeFormValues = badgeForm.watch();

  const checkboxFields = [
    "showCardLabel",
    "showVisitingLabel",
    "showFullName",
    "showVisitorCompany",
    "showEmployeeName",
    "showCompany",
    "showCheckinDate",
    "showLogo",
    "showAccessCode",
  ];

  const checkedCount = useMemo(() => {
    return checkboxFields.filter(
      (field) => badgeFormValues[field as keyof IBadgeOptions] === true,
    ).length;
  }, [badgeFormValues]);

  const isCheckboxLimitReached = checkedCount >= CHECKBOX_LIMIT;

  const isCheckboxDisabledByLimit = useCallback(
    (fieldName: string, isFieldDisabled?: boolean): boolean => {
      if (isFieldDisabled) return true;
      if (
        isCheckboxLimitReached &&
        badgeFormValues[fieldName as keyof IBadgeOptions] !== true
      ) {
        return true;
      }
      return false;
    },
    [isCheckboxLimitReached, badgeFormValues],
  );

  const [badgeQueryString, setBadgeQueryString] = useState<string>(
    qs.stringify(badgeForm.getValues()),
  );
  const [previewUrl, setPreviewUrl] = useState<string | undefined>();

  useDebounce(
    () => {
      setBadgeQueryString(() =>
        qs.stringify(pickBy(badgeFormValues, (item) => !!item)),
      );
    },
    500,
    [badgeFormValues],
  );

  const [onUpdate] = useMutation<{ input: IBadgeOptions & { client: string } }>(
    MUTATION_UPDATE_BADGE_TEMPLATE_OPTIONS,
    {
      refetchQueries: [
        {
          query: QUERY_BROTHER_PRINTER_TEMPLATE_OPTIONS,
          variables: { client: clientId },
        },
      ],
    },
  );

  const onUpload = useCallback(async (logo: IBadgeOptions["logo"]) => {
    let input: { logo?: string } = {};

    if (logo?.file) {
      await upload([logo]).then((r) => {
        input = {
          ...input,
          logo: r[0].id,
        };
      });
    }

    return input;
  }, []);

  const onSubmit = useCallback(
    async (values: IBadgeOptions) => {
      const uploadedImages = await onUpload(values?.logo);
      try {
        await onUpdate({
          variables: {
            input: {
              client: clientId,
              enabled: true,
              ...DEFAULT_BADGE_OPTIONS,
              ...values,
              ...(uploadedImages?.logo ? { logo: uploadedImages?.logo } : {}),
            },
          },
        });

        toast.success<string>(t("screens:client.brotherPrinter.toast.success"));
      } catch (error) {
        toast.error<string>(t("screens:client.brotherPrinter.toast.error"));
      }
    },
    [clientId, onUpdate, onUpload, t],
  );

  const onDisableReception = useCallback(
    () =>
      onUpdate({
        variables: {
          input: {
            client: clientId,
            enabled: false,
          },
        },
      }),
    [onUpdate, clientId],
  );

  const onRemoveLogo = useCallback(() => {
    onUpdate({
      variables: {
        input: {
          client: clientId,
          enabled: brotherPrinterTemplateOptions?.enabled ?? true,
          ...badgeFormValues,
          logo: null,
        },
      },
    });
  }, [onUpdate, clientId]);

  const apiUrl = getApiUrl();

  const api = useMemo(() => {
    return axios.create({
      baseURL: `${apiUrl}/api/v1`,
      headers: {
        "client-id": clientId,
      },
    });
  }, []);

  const getPrintPreviewDocument = useCallback(async () => {
    try {
      if (!api) {
        throw new Error("API not found!");
      }

      const response = await api.get<string>(
        `/brother-printer/print?${badgeQueryString}`,
      );

      return response?.data;
    } catch (error) {
      return undefined;
    }
  }, [api, badgeQueryString]);

  useEffect(() => {
    const getPreview = async () => {
      const data = await getPrintPreviewDocument();

      if (!data) {
        return;
      }

      // const previewUrl = URL.createObjectURL(
      //   new Blob([data], { type: "application/pdf" }),
      // );

      setPreviewUrl(data);
    };

    getPreview();
  }, [badgeQueryString]);

  if (!brotherPrinterTemplateOptions?.enabled) {
    return (
      <div
        className="tab-pane fade show active"
        id="badge-printer"
        role="tabpanel"
      >
        <ToggleBrotherPrinterButton
          onClick={() => {
            onSubmit({
              ...DEFAULT_BADGE_OPTIONS,
              enabled: true,
            });
          }}
        />
      </div>
    );
  }

  return (
    <div
      className="tab-pane fade show active"
      id="badge-printer"
      role="tabpanel"
    >
      <FormProvider {...badgeForm}>
        <form className="row" onSubmit={badgeForm.handleSubmit(onSubmit)}>
          <div className="col-12 col-md-6">
            <div className="form-group">
              <h5>
                {t(
                  "screens:client.brotherPrinter.form.title",
                  "Badge Template Options",
                )}
              </h5>
              <div className="form-group d-flex flex-column">
                <BrotherPrinterTemplateOption
                  name="showCardLabel"
                  label={t("screens:brotherPrinter.cardLabel", "Card Label")}
                  disabled={isCheckboxDisabledByLimit("showCardLabel")}
                />
                <BrotherPrinterTemplateOption
                  name="showImage"
                  disabled={isCheckboxDisabledByLimit("showImage", true)}
                  label={t(
                    "screens:brotherPrinter.visitorImage",
                    "Visitor Image",
                  )}
                />
                <BrotherPrinterTemplateOption
                  name="showFullName"
                  label={t(
                    "screens:brotherPrinter.visitorName",
                    "Visitor Name",
                  )}
                  disabled={isCheckboxDisabledByLimit("showFullName")}
                />
                <BrotherPrinterTemplateOption
                  name="showVisitingLabel"
                  label={t(
                    "screens:brotherPrinter.visitingLabel",
                    "Visiting Label",
                  )}
                  disabled={isCheckboxDisabledByLimit("showVisitingLabel")}
                />
                <BrotherPrinterTemplateOption
                  name="showVisitorCompany"
                  label={t(
                    "screens:brotherPrinter.visitorCompany",
                    "Visitor Company",
                  )}
                  disabled={isCheckboxDisabledByLimit("showVisitorCompany")}
                />
                <BrotherPrinterTemplateOption
                  name="showEmployeeName"
                  label={t("screens:brotherPrinter.hostName", "Host Name")}
                  disabled={isCheckboxDisabledByLimit("showEmployeeName")}
                />
                <BrotherPrinterTemplateOption
                  name="showCompany"
                  label={t(
                    "screens:brotherPrinter.hostCompany",
                    "Host Company",
                  )}
                  disabled={isCheckboxDisabledByLimit("showCompany")}
                />
                <BrotherPrinterTemplateOption
                  name="showCheckinDate"
                  label={t(
                    "screens:brotherPrinter.visitorCheckinDate",
                    "Date of check-in",
                  )}
                  disabled={isCheckboxDisabledByLimit("showCheckinDate")}
                />
                <div className="form-group d-flex align-items-center mb-0">
                  <label
                    htmlFor="dateFormat"
                    className="flex-shrink-0 mb-0 mr-2"
                  >
                    {t("screens:brotherPrinter.dateFormat", "Date Format:")}
                  </label>
                  <Controller
                    name="dateFormat"
                    render={({ field: { value, onChange } }) => (
                      <select
                        id="dateFormat"
                        disabled={!badgeFormValues.showCheckinDate}
                        className="form-control form-control-sm d-inline-block w-auto"
                        value={value || "DD_MM_YYYY"}
                        onChange={(e) => onChange(e.target.value)}
                      >
                        <option value="DD_MM_YYYY">DD.MM.YYYY.</option>
                        <option value="MM_DD_YYYY">MM.DD.YYYY.</option>
                        <option value="YYYY_MM_DD">YYYY.MM.DD</option>
                        <option value="DD_MM_YYYY_HH_MM">
                          DD.MM.YYYY. HH:MM
                        </option>
                        <option value="MM_DD_YYYY_HH_MM">
                          MM.DD.YYYY. HH:MM
                        </option>
                        <option value="YYYY_MM_DD_HH_MM">
                          YYYY.MM.DD HH:MM
                        </option>
                      </select>
                    )}
                  />
                </div>
                <BrotherPrinterTemplateOption
                  name="showLogo"
                  label={t(
                    "screens:brotherPrinter.hostCompanyLogo",
                    "Host Company Logo",
                  )}
                  disabled={isCheckboxDisabledByLimit("showLogo")}
                />
                <BrotherPrinterTemplateOption
                  name="showAccessCode"
                  label={t("screens:brotherPrinter.qrCode", "QR Code")}
                  disabled={isCheckboxDisabledByLimit("showAccessCode")}
                />
              </div>
              <Controller
                name="logo"
                render={({ field: { name, value, onChange } }) => (
                  <div className="form-group">
                    <label htmlFor={name}>
                      {t("client:brotherPrinter.logo.label", "Logo")}
                    </label>
                    {value && (
                      <button
                        className="btn btn-danger btn-sm ml-2"
                        onClick={(e) => {
                          e.preventDefault();
                          onRemoveLogo();
                        }}
                      >
                        {t("common:remove", "Remove")}
                      </button>
                    )}
                    <ImageCropPicker
                      allowsEditing={false}
                      name={name}
                      aspect={1080 / 545}
                      value={value}
                      onChange={(fileBase64) => onChange(fileBase64)}
                    />
                    <small className="form-text text-muted">
                      {t("screens:screen.theme.image.description")}
                    </small>
                  </div>
                )}
              />
            </div>
            {/* <pre>{JSON.stringify(badgeForm.getValues(), null, 2)}</pre> */}
          </div>
          <div className="col-12 col-md-6">
            {!previewUrl ? null : (
              <iframe
                key={previewUrl?.toString()}
                ref={badgeIFrame}
                title="badge-print-preview"
                className="border border-dark"
                src={previewUrl}
                style={{
                  width: "350px",
                  height: "212px",
                }}
              />
            )}
          </div>
          <div className="col-12">
            <input type="submit" className="btn btn-primary mr-2" />
            {isAdmin ? (
              <ToggleBrotherPrinterButton
                disable
                onClick={onDisableReception}
              />
            ) : null}
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export default memo(BrotherPrinter);
